const CONFIGURATOR_CART_DRAWER_FLAG = 'open_cart_drawer_from_configurator';
const CONFIGURATOR_CART_DRAWER_QUERY_PARAM = '_open_configurator_cart';

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.configuratorPreview = null;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    // Delegato su <cart-drawer>: #CartDrawer viene riscritto a ogni aggiornamento delle sections.
    this.addEventListener('click', (event) => {
      const closeButton = event.target.closest('[data-cart-drawer-close]');
      if (closeButton) {
        this.close();
        return;
      }

      const addButton = event.target.closest('[data-cart-drawer-add]');
      if (addButton) this.addComplementaryProduct(addButton);
    });
    this.setHeaderCartIconAccessibility();
    this.openAfterConfiguratorRedirect();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async openAfterConfiguratorRedirect() {
    let serializedState = null;
    try {
      serializedState = sessionStorage.getItem(CONFIGURATOR_CART_DRAWER_FLAG);
      sessionStorage.removeItem(CONFIGURATOR_CART_DRAWER_FLAG);
    } catch (_) {
      // Il fallback via query string funziona anche se sessionStorage non e disponibile.
    }

    const currentUrl = new URL(window.location.href);
    const requestedByQuery = currentUrl.searchParams.get(CONFIGURATOR_CART_DRAWER_QUERY_PARAM) === '1';
    if (!serializedState && !requestedByQuery) return;

    if (requestedByQuery) {
      currentUrl.searchParams.delete(CONFIGURATOR_CART_DRAWER_QUERY_PARAM);
      window.history.replaceState(window.history.state, '', currentUrl.toString());
    }

    let redirectState = {};
    if (serializedState && serializedState !== '1') {
      try {
        const parsedState = JSON.parse(serializedState);
        if (parsedState && typeof parsedState === 'object') redirectState = parsedState;
      } catch (_) {
        redirectState = {};
      }
    }

    this.configuratorPreview = redirectState;
    this.applyConfiguratorProductPreview(redirectState);
    this.applyConfiguratorPreview(redirectState);

    const openImmediately = window.matchMedia?.('(max-width: 749px)').matches;
    const cartIconTrigger = document.querySelector('#cart-icon-bubble');
    if (openImmediately) {
      if (cartIconTrigger) this.setActiveElement(cartIconTrigger);
      this.open(cartIconTrigger || undefined);
    }

    try {
      await this.refreshAfterConfiguratorRedirect();
    } catch (error) {
      console.warn('Impossibile aggiornare il cart drawer dopo il configuratore.', error);
    }

    this.applyConfiguratorPreview(this.configuratorPreview);

    if (openImmediately) {
      window.requestAnimationFrame(() => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      });
    } else {
      if (cartIconTrigger) this.setActiveElement(cartIconTrigger);
      this.open(cartIconTrigger || undefined);
    }
  }

  async refreshAfterConfiguratorRedirect() {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const sectionsUrl = new URL(rootUrl, window.location.origin);
    sectionsUrl.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');

    const response = await fetch(sectionsUrl.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Shopify Sections (${response.status})`);

    const sections = await response.json();
    if (!sections?.['cart-drawer']) {
      throw new Error('La risposta Shopify non contiene la sezione cart-drawer');
    }

    this.renderContents({ sections }, false);
  }

  applyConfiguratorPreview({ requestId, customProductId, previewDataUrl } = {}) {
    if (!requestId || !previewDataUrl) return;
    if (!/^data:image\/(?:webp|png|jpeg);base64,/i.test(previewDataUrl)) return;

    const cartItem = Array.from(this.querySelectorAll('[data-configuration-request-id]'))
      .find((item) => item.dataset.configurationRequestId === String(requestId));
    if (!cartItem) return;

    const productId = cartItem.dataset.customProductId;
    if (customProductId && productId && productId !== String(customProductId)) return;

    const mediaCell = cartItem.querySelector('.cart-item__media');
    if (!mediaCell) return;

    let image = mediaCell.querySelector('.cart-item__image');
    if (!image) {
      const detailsLink = cartItem.querySelector('.cart-item__name');
      const imagePlaceholder = mediaCell.querySelector('.cart-item__image-placeholder');
      const configureLink = mediaCell.querySelector('.cart-configure-link');
      const insertionPoint = imagePlaceholder || configureLink;
      const imageLink = document.createElement('a');
      imageLink.className = 'cart-item__link';
      imageLink.tabIndex = -1;
      imageLink.setAttribute('aria-hidden', 'true');
      imageLink.href = detailsLink?.href || window.location.href;
      imageLink.textContent = ' ';
      mediaCell.insertBefore(imageLink, insertionPoint);

      image = document.createElement('img');
      image.className = 'cart-item__image';
      image.alt = detailsLink?.textContent?.trim() || '';
      image.width = 150;
      image.height = 150;
      mediaCell.insertBefore(image, insertionPoint);
      imagePlaceholder?.remove();
    }

    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.loading = 'eager';
    image.src = previewDataUrl;
  }

  applyConfiguratorProductPreview({ customProductId, previewDataUrl } = {}) {
    if (!customProductId || !previewDataUrl) return;
    if (!/^data:image\/(?:webp|png|jpeg);base64,/i.test(previewDataUrl)) return;

    const productInfo = Array.from(document.querySelectorAll('product-info[data-product-id]'))
      .find((element) => element.dataset.productId === String(customProductId));
    if (!productInfo) return;

    const product = productInfo.querySelector('.product');
    const mediaWrapper = product?.querySelector('.product__media-wrapper');
    if (!product || !mediaWrapper) return;

    const currentImages = mediaWrapper.querySelectorAll('.product__media-list img');
    if (currentImages.length > 0) {
      currentImages.forEach((image) => {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.loading = 'eager';
        image.src = previewDataUrl;
      });
      return;
    }

    const mediaList = mediaWrapper.querySelector('.product__media-list');
    if (!mediaList) return;

    product.classList.remove('product--no-media');
    product.classList.add('grid--2-col-tablet');

    const mediaItem = document.createElement('li');
    mediaItem.className = 'product__media-item grid__item slider__slide is-active product__media-item--single';

    const mediaContainer = document.createElement('div');
    mediaContainer.className = 'product-media-container media-type-image media-fit-contain global-media-settings gradient';
    mediaContainer.style.setProperty('--ratio', '1');
    mediaContainer.style.setProperty('--preview-ratio', '1');

    const media = document.createElement('div');
    media.className = 'product__media media media--transparent';

    const image = document.createElement('img');
    image.src = previewDataUrl;
    image.alt = productInfo.querySelector('.product__title')?.textContent?.trim() || '';
    image.loading = 'eager';
    image.width = 1024;
    image.height = 1024;

    media.appendChild(image);
    mediaContainer.appendChild(media);
    mediaItem.appendChild(mediaContainer);
    mediaList.appendChild(mediaItem);
  }

  async addComplementaryProduct(button) {
    if (button.disabled) return;
    const variantId = Number(button.dataset.variantId);
    if (!Number.isSafeInteger(variantId) || variantId <= 0) return;

    const addedMessage = button.dataset.addedMessage || '';
    const errorMessage = button.dataset.errorMessage || window.cartStrings?.error || '';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const errorsElement = this.querySelector('#CartDrawer-CartErrors');
    if (errorsElement) errorsElement.textContent = '';

    try {
      const response = await fetch(routes.cart_add_url, {
        ...fetchConfig('javascript'),
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.status || !state.sections) {
        throw new Error(state.description || state.message || window.cartStrings?.error);
      }

      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-complementary',
        productVariantId: String(variantId),
        cartData: state,
      });
      this.renderContents({ id: state.id, sections: state.sections }, false);

      const liveRegion = this.querySelector('#CartDrawer-LiveRegionText');
      if (liveRegion) liveRegion.textContent = addedMessage;
      this.querySelector(`[data-cart-item-variant-id="${variantId}"] .cart-item__name`)?.focus();
    } catch (error) {
      // Il drawer non e stato riscritto: il pulsante e ancora nel DOM e va riabilitato.
      button.disabled = false;
      button.removeAttribute('aria-busy');
      const currentErrors = this.querySelector('#CartDrawer-CartErrors');
      if (currentErrors) currentErrors.textContent = error.message || errorMessage;
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const isConfiguratorReturn = document.documentElement.classList.contains('configurator-cart-return');
    const activateDrawer = () => {
      this.classList.add('animate', 'active');
      document.documentElement.classList.remove('configurator-cart-return');
      window.renderPayPalCartMessage?.();
    };

    if (isConfiguratorReturn) {
      activateDrawer();
    } else {
      // Il frame separato permette alla transizione standard di partire in modo affidabile.
      setTimeout(activateDrawer);
    }

    const applyFocusTrap = () => {
      const containerToTrapFocusOn = document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    if (isConfiguratorReturn) {
      window.requestAnimationFrame(applyFocusTrap);
    } else {
      this.addEventListener('transitionend', applyFocusTrap, { once: true });
    }

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    document.documentElement.classList.remove('configurator-cart-return');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      if (!parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.applyConfiguratorPreview(this.configuratorPreview);
    window.renderPayPalCartMessage?.(parsedState);

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (shouldOpen) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
